// src/hooks/useOportunidadesEscondidas.js
// Sessão 4.9 — Widget Oportunidades Escondidas
//
// Descobre empresas que têm emails ativos (não-lixo) mas ainda não têm nenhuma
// oferta cadastrada no sistema. Threshold mínimo de emails configurável.
//
// Estratégia: 2 queries paralelas + cruzamento no cliente.
//   Query 1: lista de empresa_ids que JÁ têm oferta (exclusão).
//   Query 2: emails não-lixo com empresa_id (inclusão).
// Agregação em memória: top N por qtd_emails.
//
// Por que cliente e não RPC: ~1319 emails hoje. Performance aceitável.
// Dívida consciente: se o volume chegar a 10k+ emails, migrar para função SQL.

import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

export function useOportunidadesEscondidas({ minEmails = 2 } = {}) {
  const [oportunidades, setOportunidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let ativo = true;

    (async () => {
      setLoading(true);
      setErro(null);

      try {
        // Query 1: todas as empresa_ids que já têm oferta (não entram no widget)
        const { data: ofertas, error: e1 } = await supabase
          .from('ofertas')
          .select('empresa_id')
          .not('empresa_id', 'is', null);
        if (e1) throw e1;

        const idsComOferta = new Set((ofertas || []).map((o) => o.empresa_id));

        // Query 2: emails não-lixo com empresa_id, já trazendo nome da empresa
        const { data: emails, error: e2 } = await supabase
          .from('emails_gmail')
          .select('empresa_id, from_email, data_envio, empresas!emails_gmail_empresa_id_fkey(id, company_name)')
          .not('empresa_id', 'is', null)
          .neq('acao_humana', 'lixo');
        if (e2) throw e2;

        // Agrupar por empresa, excluindo as que já têm oferta
        const grupos = new Map();
        const hoje = new Date();

        for (const em of emails || []) {
          if (idsComOferta.has(em.empresa_id)) continue;

          let g = grupos.get(em.empresa_id);
          if (!g) {
            g = {
              empresa_id: em.empresa_id,
              empresa_nome: em.empresas?.company_name || '(sem nome)',
              qtd_emails: 0,
              _contatos: new Set(),
              ultimo_email: null,
            };
            grupos.set(em.empresa_id, g);
          }
          g.qtd_emails += 1;
          if (em.from_email) g._contatos.add(em.from_email.toLowerCase().trim());
          if (em.data_envio) {
            const d = new Date(em.data_envio);
            if (!g.ultimo_email || d > g.ultimo_email) g.ultimo_email = d;
          }
        }

        // Consolidar + filtrar por threshold + ordenar
        const resultado = Array.from(grupos.values())
          .map((g) => {
            const dias_desde_ultimo = g.ultimo_email
              ? Math.floor((hoje - g.ultimo_email) / (1000 * 60 * 60 * 24))
              : null;
            return {
              empresa_id: g.empresa_id,
              empresa_nome: g.empresa_nome,
              qtd_emails: g.qtd_emails,
              qtd_contatos: g._contatos.size,
              ultimo_email: g.ultimo_email,
              dias_desde_ultimo,
            };
          })
          .filter((g) => g.qtd_emails >= minEmails)
          .sort((a, b) => {
            // Ordenação: mais emails primeiro; em empate, último email mais recente
            if (b.qtd_emails !== a.qtd_emails) return b.qtd_emails - a.qtd_emails;
            const da = a.ultimo_email ? a.ultimo_email.getTime() : 0;
            const db = b.ultimo_email ? b.ultimo_email.getTime() : 0;
            return db - da;
          });

        if (ativo) setOportunidades(resultado);
      } catch (err) {
        if (ativo) setErro(err);
      } finally {
        if (ativo) setLoading(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [minEmails, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return { oportunidades, loading, erro, refresh };
}
